import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './InvitationsPage.css';

function getInviter(invitation) {
  return invitation?.inviter || invitation?.invited_by || invitation?.user || {};
}

function getInviterName(invitation) {
  const inviter = getInviter(invitation);
  return inviter.inviter_name || inviter.name || inviter.full_name || 'Unknown';
}

function getInviterPublicId(invitation) {
  const inviter = getInviter(invitation);
  return inviter.inviter_public_identifier || inviter.public_identifier || inviter.publicIdentifier || null;
}

function getSharedSecret(invitation) {
  return invitation?.specifics?.shared_secret || invitation?.shared_secret || null;
}

function formatDate(value) {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function InvitationsPage({ accountId }) {
  const [activeTab, setActiveTab] = useState('received');

  const [receivedItems, setReceivedItems] = useState([]);
  const [receivedCursor, setReceivedCursor] = useState(null);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [receivedError, setReceivedError] = useState('');
  const [actionById, setActionById] = useState({});

  const [pendingInvitations, setPendingInvitations] = useState(null);
  const [pendingInvitationsLoading, setPendingInvitationsLoading] = useState(false);
  const [withdrawingInvitations, setWithdrawingInvitations] = useState(false);
  const [withdrawCount, setWithdrawCount] = useState(10);

  const loadReceivedInvitations = useCallback(async ({ append = false, cursor = null } = {}) => {
    if (!accountId) return;
    setReceivedLoading(true);
    setReceivedError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/linkedin/invitations/received/${accountId}`, {
        params: { limit: 25, cursor: cursor || undefined },
        timeout: 30000
      });

      if (!response.data.success) {
        setReceivedError(response.data.message || 'Failed to load received invitations');
        return;
      }

      setReceivedItems(prev => append ? [...prev, ...(response.data.items || [])] : (response.data.items || []));
      setReceivedCursor(response.data.cursor || null);
    } catch (error) {
      setReceivedError(error.response?.data?.message || error.message || 'Failed to load received invitations');
    } finally {
      setReceivedLoading(false);
    }
  }, [accountId]);

  const loadPendingInvitations = useCallback(async () => {
    if (!accountId) return;
    setPendingInvitationsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/workflow/invitations/pending/${accountId}`, {
        params: { preview_limit: 8 },
        timeout: 30000
      });
      if (response.data.success) {
        setPendingInvitations(response.data);
      }
    } catch (error) {
      setPendingInvitations({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to load pending invitations'
      });
    } finally {
      setPendingInvitationsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadReceivedInvitations();
    loadPendingInvitations();
  }, [loadReceivedInvitations, loadPendingInvitations]);

  const handleReceivedAction = async (invitation, action) => {
    const invitationId = invitation.id || invitation.invitation_id;
    const sharedSecret = getSharedSecret(invitation);
    if (!invitationId || !sharedSecret) {
      alert('Missing invitation_id or shared_secret. Cannot process this invitation.');
      return;
    }

    if (action === 'decline') {
      const confirmed = window.confirm(`Decline invitation from ${getInviterName(invitation)}?`);
      if (!confirmed) return;
    }

    setActionById(prev => ({ ...prev, [invitationId]: action }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/linkedin/invitations/received/${accountId}/${invitationId}/${action}`,
        { shared_secret: sharedSecret },
        { timeout: 30000 }
      );

      if (response.data.success) {
        setReceivedItems(prev => prev.filter(item => (item.id || item.invitation_id) !== invitationId));
      } else {
        alert(response.data.message || `Failed to ${action} invitation`);
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || `Failed to ${action} invitation`);
    } finally {
      setActionById(prev => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }
  };

  const handleWithdrawOldest = async () => {
    const count = Math.min(20, Math.max(1, parseInt(withdrawCount, 10) || 1));
    const confirmed = window.confirm(`Withdraw the ${count} oldest pending LinkedIn invitations?`);
    if (!confirmed) return;

    setWithdrawingInvitations(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/workflow/invitations/withdraw-oldest/${accountId}`, {
        count,
        pace: false
      });
      if (response.data.success) {
        alert(`Withdraw complete: ${response.data.withdrawn || 0} withdrawn, ${response.data.errors || 0} errors.`);
        await loadPendingInvitations();
      } else {
        alert(response.data.message || 'Withdraw failed');
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Withdraw failed');
    } finally {
      setWithdrawingInvitations(false);
    }
  };

  const renderReceivedTab = () => (
    <div className="invitations-panel">
      <div className="invitations-panel-header">
        <div>
          <h2>Received Invitations</h2>
          <p>Review connection invitations sent to this LinkedIn account.</p>
        </div>
        <button className="invitations-secondary-button" onClick={() => loadReceivedInvitations()} disabled={receivedLoading}>
          {receivedLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {receivedError && <div className="invitations-alert error">{receivedError}</div>}

      <div className="invitations-table-wrapper">
        <table className="invitations-table">
          <thead>
            <tr>
              <th>Inviter</th>
              <th>Received</th>
              <th>Message</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {receivedLoading && receivedItems.length === 0 && (
              <tr>
                <td colSpan={4} className="invitations-empty">Loading received invitations...</td>
              </tr>
            )}
            {!receivedLoading && receivedItems.length === 0 && (
              <tr>
                <td colSpan={4} className="invitations-empty">No received invitations.</td>
              </tr>
            )}
            {receivedItems.map((invitation) => {
              const invitationId = invitation.id || invitation.invitation_id;
              const publicId = getInviterPublicId(invitation);
              const action = actionById[invitationId];
              return (
                <tr key={invitationId}>
                  <td>
                    <div className="invitations-person">
                      <div className="invitations-avatar">{getInviterName(invitation).charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="invitations-name">{getInviterName(invitation)}</div>
                        {publicId && (
                          <a href={`https://www.linkedin.com/in/${publicId}`} target="_blank" rel="noreferrer">
                            {publicId}
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{formatDate(invitation.parsed_datetime || invitation.created_at)}</td>
                  <td className="invitations-message">{invitation.invitation_text || invitation.message || '-'}</td>
                  <td className="invitations-actions-cell">
                    <button
                      className="invitations-accept-button"
                      onClick={() => handleReceivedAction(invitation, 'accept')}
                      disabled={Boolean(action)}
                    >
                      {action === 'accept' ? 'Accepting...' : 'Accept'}
                    </button>
                    <button
                      className="invitations-decline-button"
                      onClick={() => handleReceivedAction(invitation, 'decline')}
                      disabled={Boolean(action)}
                    >
                      {action === 'decline' ? 'Declining...' : 'Decline'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {receivedCursor && (
        <button
          className="invitations-load-more"
          onClick={() => loadReceivedInvitations({ append: true, cursor: receivedCursor })}
          disabled={receivedLoading}
        >
          {receivedLoading ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  );

  const renderSentTab = () => (
    <div className="invitations-panel">
      <div className="invitations-panel-header">
        <div>
          <h2>Sent Pending</h2>
          <p>Monitor pending connection requests and withdraw the oldest ones when needed.</p>
        </div>
        <button className="invitations-secondary-button" onClick={loadPendingInvitations} disabled={pendingInvitationsLoading || withdrawingInvitations}>
          {pendingInvitationsLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {pendingInvitations?.message && <div className="invitations-alert error">{pendingInvitations.message}</div>}

      {pendingInvitations?.success !== false && pendingInvitations ? (
        <>
          <div className="pending-invitations-grid">
            <div className={`pending-stat-card ${pendingInvitations.over_threshold ? 'danger' : ''}`}>
              <div className="pending-stat-label">LinkedIn Pending</div>
              <div className="pending-stat-value">{pendingInvitations.pending}</div>
              <div className="pending-stat-note">Threshold: {pendingInvitations.threshold}</div>
            </div>
            <div className="pending-stat-card">
              <div className="pending-stat-label">Local Tracked</div>
              <div className="pending-stat-value">{pendingInvitations.local_pending}</div>
              <div className="pending-stat-note">Campaign records matched locally</div>
            </div>
            <div className="pending-stat-card">
              <div className="pending-stat-label">Auto Withdraw Target</div>
              <div className="pending-stat-value">{pendingInvitations.target_after_withdraw}</div>
              <div className="pending-stat-note">Daily cap: {pendingInvitations.max_withdrawals_per_day}</div>
            </div>
          </div>

          <div className="withdraw-controls">
            <label>
              Withdraw oldest
              <input
                type="number"
                min="1"
                max="20"
                value={withdrawCount}
                onChange={(e) => setWithdrawCount(e.target.value)}
                disabled={withdrawingInvitations}
              />
            </label>
            <button
              className="invitations-danger-button"
              onClick={handleWithdrawOldest}
              disabled={withdrawingInvitations || pendingInvitations.pending <= 0}
            >
              {withdrawingInvitations ? 'Withdrawing...' : 'Withdraw Oldest'}
            </button>
          </div>

          {pendingInvitations.oldest && pendingInvitations.oldest.length > 0 && (
            <details className="oldest-invitations">
              <summary>Oldest pending invitations</summary>
              <div className="oldest-invitations-list">
                {pendingInvitations.oldest.map((item) => (
                  <div key={item.invitation_id} className="oldest-invitation-row">
                    <span>{item.name || 'unknown'}</span>
                    <span>{formatDate(item.invitation_sent_at)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      ) : (
        <div className="invitations-empty">
          {pendingInvitationsLoading ? 'Loading pending invitations...' : 'Pending invitation data not loaded yet.'}
        </div>
      )}
    </div>
  );

  return (
    <div className="invitations-page">
      <div className="invitations-container">
        <div className="invitations-header">
          <h1>LinkedIn Invitations</h1>
          <div className="invitations-account">Account: {accountId}</div>
        </div>

        <div className="invitations-tabs">
          <button
            className={activeTab === 'received' ? 'active' : ''}
            onClick={() => setActiveTab('received')}
          >
            Received
          </button>
          <button
            className={activeTab === 'sent' ? 'active' : ''}
            onClick={() => setActiveTab('sent')}
          >
            Sent Pending
          </button>
        </div>

        {activeTab === 'received' ? renderReceivedTab() : renderSentTab()}
      </div>
    </div>
  );
}

export default InvitationsPage;
